import { styled } from "@linaria/react"
import { Link } from "@tanstack/react-router"
import { useEffect } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { Centered } from "@/components/Centered/Centered.ts"
import { Vertical } from "@/components/Flex.ts"
import { HmmError, SvgIllustration } from "@/illustrations/Illustrations.ts"
import {
  OrgNotFoundException,
  ProjectNotFoundException,
  TaskNotFoundException,
  UserInOrgNotFoundException
} from "@/lib/client/types.ts"
import { formatErrorMessage, pageTitle } from "@/lib/displayUtils.ts"
import { getLogger } from "@/lib/logging.ts"
import { MatterLogoAndName } from "@/routes/auth/MatterLogoAndName.tsx"
import { Page } from "@/routes/auth/Page.tsx"
import { PageBackgroundGradient } from "@/routes/auth/PageBackgroundGradient.ts"
import { PageBackground, PageContainer, PageContent } from "@/routes/auth/PageStyles.ts"
import { Navigator } from "@/routes/Navigator.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { ErrorComponentProps } from "@tanstack/react-router"

const log = getLogger("ErrorScene")

export const ErrorScene = ({ error }: ErrorComponentProps) => {
  useEffect(() => {
    if (error instanceof OrgNotFoundException
      || error instanceof UserInOrgNotFoundException
      || error instanceof ProjectNotFoundException
      || error instanceof TaskNotFoundException
    ) {
      return
    }

    log.error("Unhandled error:", error)
  }, [error])

  if (error instanceof OrgNotFoundException) {
    return (
      <NotFoundErrorPage
        title="Organization is missing"
        subtitle="This organization is missing — hopefully it's not the one that matters."
        buttonText="Back to organizations"
        buttonLink={Navigator.home()}
      />
    )
  }

  if (error instanceof UserInOrgNotFoundException) {
    return (
      <NotFoundErrorPage
        title="Workspace is private"
        subtitle="Ask the person who sent you this link to invite you before you can join this workspace."
      />
    )
  }

  if (error instanceof ProjectNotFoundException) {
    return (
      <NotFoundErrorPage
        title="Project is missing"
        subtitle="This project is missing — hopefully it's not the one that matters."
        buttonText="Go to all projects"
        buttonLink={error.orgId ? Navigator.orgHome(error.orgId) : undefined}
      />
    )
  }

  if (error instanceof TaskNotFoundException) {
    const hasProjectContext = error.orgId && error.projectKey
    return (
      <NotFoundErrorPage
        title="Task is missing"
        subtitle="This task is missing — hopefully it's not the one that matters."
        buttonText={hasProjectContext ? "Go to project tasks" : undefined}
        buttonLink={hasProjectContext ? Navigator.project(error.orgId, { key: error.projectKey }) : undefined}
      />
    )
  }

  return <ErrorPage error={error} />
}

export const Error = ({ error }: { error: Error }) => {
  return (
    <ErrorMessage>
      <strong>{formatErrorMessage(error, error.message)}</strong>

      {import.meta.env.DEV && <ErrorStack>{error.stack}</ErrorStack>}
    </ErrorMessage>
  )
}

const ErrorPage = ({ error }: { error: Error }) => {
  return (
    <>
      <PageContainer>
        <PageContent twoPaneLayout={false}>
          <Centered gap="16px">
            <title>{pageTitle("Error")}</title>
            <MatterLogoAndName />
            <ErrorHeading>An error has occurred</ErrorHeading>
            <ErrorWrapper>
              <Error error={error} />
            </ErrorWrapper>
          </Centered>
        </PageContent>
      </PageContainer>

      <PageBackground background={PageBackgroundGradient.Orgs} />
    </>
  )
}

const NotFoundErrorPage = ({ title, subtitle, buttonText, buttonLink }: {
  title: string
  subtitle: string
  buttonText?: string
  buttonLink?: ReturnType<(typeof Navigator.home)> | ReturnType<(typeof Navigator.orgHome)> | ReturnType<(typeof Navigator.project)>
}) => {
  return (
    <Page backgroundGradient={PageBackgroundGradient.Orgs} orgPage={false} withMainMenu={false} withUserMenu={false}>
      <title>{pageTitle(title)}</title>
      <Centered gap="24px">
        <HmmError className={SvgIllustration} />
        <ErrorSubtext>{subtitle}</ErrorSubtext>
        <ButtonWrapper>
          {buttonLink ? (
            <Link {...buttonLink} viewTransition>
              <Button kind="secondary" size="large" tabIndex={-1}>
                {buttonText ?? "Go back home"}
              </Button>
            </Link>
          ) : (
            <Link {...Navigator.home()} viewTransition>
              <Button kind="secondary" size="large" tabIndex={-1}>
                {buttonText ?? "Go back home"}
              </Button>
            </Link>
          )}
        </ButtonWrapper>
      </Centered>
    </Page>
  )
}

const ErrorSubtext = styled.p`
  margin: 0;

  font-size: 18px;
  color: var(--secondary-text-color);

  text-align: center;
`

const ButtonWrapper = styled.div`
  margin-top: 16px;
`

const ErrorHeading = styled.h1`
  max-width: 600px;
  margin: 0;

  font-size: 54px;
  font-weight: 600;
  line-height: 60px;
  letter-spacing: -0.036px;

  text-align: center;
`

const ErrorMessage = styled(Vertical)`
  gap: 8px;

  color: var(--error-color);
  background: var(--error-color-10);

  border-radius: var(--border-radius-16);

  padding: 16px;
`

const ErrorStack = styled.pre`
  ${textStyles.pre};
`

const ErrorWrapper = styled.div`
  margin-top: 16px;
`